import { Component, OnInit, ElementRef, OnDestroy, signal, computed } from '@angular/core';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  UserInfo,
  LayoutVars,
  Permission,
  Role,
  LEGACY_TRANSFER_ROUTE_ROLES,
} from 'src/app/store/session/session.store';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { SessionService } from 'src/app/store/session/session.service';
import { SessionQuery } from 'src/app/store/session/session.query';
import { ActivatedRoute, Router, NavigationEnd, Params, IsActiveMatchOptions } from '@angular/router';
import { delay, filter, retryWhen, take } from 'rxjs/operators';
import { OemDealerService } from '@app-services/oem-dealer.service';
import { MarketPlacePersonalizationUtils } from '../marketplace-personalization';
import { StompService, LiveBidItem } from '@app-services/stomp.service';
import { OpenService } from '@app-services/open.service';
import { reloadCurrentRoute } from '@app-utils/router-utils';
import { AppRouteReuseStrategy } from '@app-shared/strategies/app-route-reuse.strategy';

declare const $;

@Component({
  selector: 'app-shopkeeper-header',
  templateUrl: './shopkeeper-header.component.html',
  styleUrls: ['./shopkeeper-header.component.scss'],
})
export class ShopkeeperHeaderComponent implements OnInit, OnDestroy {
  isAuthenticated$: Observable<boolean>;
  userInfo$: Observable<UserInfo>;
  userInfo: UserInfo;
  layoutVars: LayoutVars;
  childnavOpen: boolean;
  fastQuoteCount: number;
  requestedAuctionRefundsCount: number;
  subscription: any;

  notifications: any[];
  hasLatePayment = false;

  activeMenu = '';
  logoUrl: string;
  numberPlate: string;

  stompSubscription;

  canSeeSellReport = false;
  returnUrl: string;

  private readonly userInfoSignal = signal<UserInfo | null>(null);

  readonly vendedoresAdminLink = computed<string[]>(() =>
    this.canSeeOemDealersAdminFrom(this.userInfoSignal())
      ? ['/admin/advertisers/list']
      : ['/vender/admin'],
  );

  readonly vendedoresAdminQueryParams = computed<Params | null>(() => {
    if (this.canSeeOemDealersAdminFrom(this.userInfoSignal())) {
      return null;
    }
    const oemDealerId = this.userInfoSignal()?.resolveContextOemDealerId();
    return oemDealerId
      ? { menu: 'admin', oemDealerId }
      : { menu: 'admin' };
  });

  constructor(
    private navService: NavigationService,
    private sessionQuery: SessionQuery,
    private sessionService: SessionService,
    private openService: OpenService,
    private router: Router,
    private route: ActivatedRoute,
    private oemDealerService: OemDealerService,
    private elRef: ElementRef,
    private stomp: StompService,
    private routeReuse: AppRouteReuseStrategy,
    private toastr: ToastrService,
  ) {
    this.returnUrl = this.router.url;
  }

  navReload(): void {
    this.routeReuse.forceReloadOnce();
  }

  ngOnDestroy(): void {
    if (this.stompSubscription) {
      // this.stompSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.openService.getLayoutVars().subscribe(layoutVars => {
      this.layoutVars = layoutVars;

      if (layoutVars) {
        if (layoutVars.logoUrl) {
          this.logoUrl = layoutVars.logoUrl;
        }
        MarketPlacePersonalizationUtils.initCssVars(this.elRef, layoutVars);
      }
    });

    this.isAuthenticated$ = this.sessionQuery.isAuthenticated$;
    this.userInfo$ = this.sessionQuery.userInfo$;
    this.userInfo$.subscribe((userInfo) => {
      this.userInfo = userInfo;
      this.userInfoSignal.set(userInfo);
    });
    this.userInfo$
      .pipe(filter((userInfo) => userInfo !== null))
      .subscribe((userInfo) => {
        this.navService.getRequestedAuctionRefundsCount();

        if (userInfo.hasLatePayment) {
          document.body.classList.add('client-has-late-payment');
          this.hasLatePayment = userInfo.hasLatePayment;
        } else {
          document.body.classList.remove('client-has-late-payment');
          this.hasLatePayment = userInfo.hasLatePayment;
        }

        if (userInfo.hasPermissionMenuVehicle()) {
          this.getFastQuoteCount();
          this.stompSubscription = this.stomp.dealerTopic(userInfo)
            .pipe(retryWhen(errors => {
              return errors.pipe(
                delay(5000),
                take(3)
              )
            }))
            .subscribe(resp => {
              this.handleFastQuoteCountStomp(resp);
            })
        }


        this.canSeeSellReport = userInfo.hasPermissionDeskSell() || userInfo.hasRole('LOCATION_MANAGER');
      });

    this.childnavOpen = false;

    this.setActiveMenu();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.canSeeCancellationRefundsAdmin()) {
          this.navService.getRequestedAuctionRefundsCount();
        }
        setTimeout(() => this.setActiveMenu(), 0);
        // this.setActiveMenu();
      }
    });

    this.navService.requestedAuctionRefundsCount$.subscribe(count => {
      this.requestedAuctionRefundsCount = count;
    });
  }

  getFastQuoteCount() {
    this.navService.fastQuoteCount$.subscribe(resp => {
      this.fastQuoteCount = resp;
    })
  }

  handleFastQuoteCountStomp(fastQuote: LiveBidItem): void {
    if (fastQuote.event === 'FAST_QUOTE_ACTIVATED') {
      this.navService.incrementFastQuoteCount();
    }

    if (fastQuote.event === 'FAST_QUOTE_CANCELED' || fastQuote.event === 'FAST_QUOTE_SOLD' || fastQuote.event === 'FAST_QUOTE_EXPIRED') {
      this.navService.decrementFastQuoteCount();
    }
  }

  routeToStart(): string {

    if (this.layoutVars && this.layoutVars.logoDestinationUrl) {
      return this.layoutVars.logoDestinationUrl;
    }
    const baseDomain = window.location.origin;
    return baseDomain + '/arrematar/home';
  }

  setActiveMenu() {
    const subsetMatch: IsActiveMatchOptions = {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    };

    if (this.router.isActive('/arrematar', subsetMatch)) {
      this.activeMenu = 'ARREMATAR';
    }

    if (this.router.isActive('/vender', subsetMatch)) {
      if (this.router.url.includes('/vender/admin')) {
        const menu = this.router.parseUrl(this.router.url).queryParams['menu'];
        // menu=buy|sell|admin: origem no header; define qual aba destacar em /vender/admin
        if (menu === 'admin') {
          this.activeMenu = 'ADMINISTRACAO';
        } else if (menu === 'buy') {
          this.activeMenu = 'CAPTAR';
        } else {
          this.activeMenu = 'VENDER';
        }
      } else {
        this.activeMenu = 'VENDER';
      }
    }

    if (this.router.isActive('/captar', subsetMatch)) {
      this.activeMenu = 'CAPTAR';
    }

    if (
      this.router.isActive('/dashboard/eval', subsetMatch) ||
      this.router.url.includes('/cotacao-rapida/interessados')
    ) {
      this.activeMenu = 'CAPTAR';
    }

    if (this.router.isActive('/admin', subsetMatch)) {
      this.activeMenu = 'ADMINISTRACAO';
    }
  }

  getNumberPlate() {
    if (!this.numberPlate) {
      return;
    }
    this.toggleSidebar();
    this.router.navigate(['/captar/buscar-placa', this.numberPlate]);
  }

  toggleSidebar() {
    const state = this.navService.sidebarState;
    if (state.childnavOpen) {
      this.childnavOpen = false;
      return (state.childnavOpen = false);
    } else {
      this.childnavOpen = true;
      return (state.childnavOpen = true);
    }
  }

  onClickMenu(menu) {
    if (this.activeMenu !== menu) {
      this.activeMenu = '';
    }
  }

  onClickOemDealers(oemDealer) {
    this.sessionService.updateOemDealersSelected(oemDealer);
    reloadCurrentRoute(this.router);
  }

  isDealerSelected(userInfo: UserInfo, oemDealer) {
    return (
      userInfo.oemDealerGroupSelected &&
      userInfo.oemDealerGroupSelected.id === oemDealer.id
    );
  }

  onClickLinkSoldPayment() {
    this.router.navigateByUrl(
      '/arrematar/arrematados?expandedStatuses=["SOLD_NO_PAYMENT"]'
    );
  }

  hasPermissionMenuAvaliador() {
    return this.userInfo?.hasRoleAvaliador();
  }

  hasPermissionMenuArremate() {
    return this.userInfo?.hasRoleArremate() || (this.userInfo === undefined && this.layoutVars?.allowPublicAccess);
  }

  hasPermissionDeskSell(): boolean {
    return this.userInfo?.hasPermissionDeskSell();
  }

  hasPermissionOemDealerManager(): boolean {
    return this.userInfo?.hasPermissionOemDealerManager();
  }

  hasPermissionOemDealerDashboard(): boolean {
    return this.userInfo?.hasPermissionOemDealerDashboard();
  }

  hasOemDealerSelected(): boolean {
    return !!this.userInfo?.resolveContextOemDealerId();
  }


  navigateToOemDealerAdmin(menu: 'buy' | 'sell' | 'admin'): void {
    this.toggleSidebar();
    const user = this.userInfo;
    if (!user) {
      return;
    }
    const oemDealerId = user.resolveContextOemDealerId();
    if (!oemDealerId) {
      this.toastr.warning('Selecione um ambiente no topo da página para acessar a administração.');
      return;
    }
    const match =
      user.oemDealers?.find((o) => o.id === oemDealerId) ??
      (user.oemDealerId === oemDealerId && user.oemDealerName
        ? {
          id: oemDealerId,
          name: user.oemDealerName,
          inactive: false,
          allowLeadGeneration: user.showAuctionLeads ?? false,
        }
        : null);
    if (match) {
      localStorage.setItem('oemDealerSelected', JSON.stringify(match));
      this.sessionService.updateOemDealersSelected(match);
    }
    this.router.navigate(['/vender/admin'], { queryParams: { menu, oemDealerId } });
  }

  hasPermissionToViewTradeinDashboard() {
    const hasLegacy = this.userInfo?.hasLegacySellRoles() || this.userInfo?.isAdmin();
    const hasPermission = this.userInfo?.hasPermission(Permission.VIEW_TRADEIN_DASHBOARD);
    return hasPermission || hasLegacy;
  }

  hasOemDealerWithLeads(): boolean {
    let result = false;
    if (this.userInfo?.oemDealers) {
      result = this.userInfo?.oemDealers.some((oem) => oem.allowLeadGeneration);
    }

    return result || this.userInfo?.showAuctionLeads;
  }

  hasPermissionMenuVehicle() {
    return this.userInfo?.hasPermissionMenuVehicle();
  }

  hasPermissionToViewDeletedVehicles() {
    return this.userInfo?.hasPermissionOrLegacy(Permission.VIEW_DELETED_VEHICLES);
  }

  hasPermissionToViewLeads() {
    const hasLegacy = this.userInfo?.hasLegacySellRoles() || this.userInfo?.isAdmin();
    const hasPermission = this.userInfo?.hasPermission(Permission.VIEW_LEADS_VEHICLES);
    return hasPermission || hasLegacy;
  }

  hasPermissionMenuVender() {
    return this.userInfo?.hasPermissionMenuVender() ?? false;
  }

  hasPermissionCaptar(): boolean {
    return this.userInfo?.hasPermissionOrLegacy(Permission.CREATE_EVALUATION)
      || this.userInfo?.hasLegacyBuyRoles()
      || this.userInfo?.isAdmin()
      || false;
  }

  hasPermissionAvaliarHistory() {
    const hasRoles = this.userInfo?.hasRoles(
      Role.ADMIN,
      Role.SELLER_MANAGER,
      Role.DESK_BUY,
      Role.OEM_DEALER_MANAGER,
      Role.EVAL_DEALER_MANAGER,
      Role.SUPER_EVALUATOR,
      Role.SELLER
    );

    const hasPermission = this.userInfo?.hasAnyPermissionOrLegacy(
      Permission.VIEW_EXPIRED_MY_EVALUATIONS,
      Permission.VIEW_EXPIRED_EVALUATIONS,
      Permission.VIEW_CANCELLED_MY_EVALUATIONS,
      Permission.VIEW_CANCELLED_EVALUATIONS,
      Permission.VIEW_PURCHASED_MY_EVALUATIONS,
      Permission.VIEW_PURCHASED_EVALUATIONS,
      Permission.VIEW_ARCHIVED_EVALUATIONS
    );

    return hasRoles || hasPermission;
  }

  hasPermissionEvalLeads() {
    const hasRoles = this.userInfo?.hasRoles(
      Role.ADMIN,
      Role.SELLER_MANAGER,
      Role.SELLER,
      Role.DESK_BUY,
      Role.OEM_DEALER_MANAGER,
      Role.EVAL_DEALER_MANAGER
    );

    const hasPermission = this.userInfo?.hasPermission(Permission.VIEW_LEADS_VEHICLES);

    return hasRoles || hasPermission;
  }

  hasPermissionViewRescues(): boolean {
    const hasLegacy = this.userInfo?.hasRoles(Role.ADMIN, Role.OEM_DEALER_MANAGER);
    const hasPermission = this.userInfo?.hasPermission(Permission.VIEW_RESCUES);
    return !!hasLegacy || !!hasPermission;
  }

  hasPermissionEvalDashboard(): boolean {
    const hasLegacyRoles = this.userInfo?.hasRoles(
      Role.ADMIN,
      Role.SELLER_MANAGER,
      Role.DESK_BUY,
      Role.OEM_DEALER_MANAGER,
      Role.EVAL_DEALER_MANAGER
    );

    const hasPermission = this.userInfo?.hasPermission(Permission.VIEW_EVALUATION_DASHBOARD)
      || this.userInfo?.hasPermission(Permission.DASHBOARD_MAIN_PAGE);

    return hasPermission || hasLegacyRoles;
  }

  hasPermissionRepasse() {
    const hasLegacy = this.userInfo?.hasLegacySellRoles() || this.userInfo?.isAdmin();

    const hasPermission = this.userInfo?.hasPermission(Permission.VIEW_STOCK);

    return !!hasLegacy || !!hasPermission;
  }

  hasPermissionTransferVehicles() {
    return this.userInfo?.hasAnyPermissionOrLegacy(Permission.VIEW_TRANSFER_VEHICLES);
  }

  hasPermissionImportVehicles() {
    return this.userInfo?.hasPermissionOrLegacy(Permission.IMPORT_MASS_TRANSFERS);
  }

  showAdminMenuTab(): boolean {
    return (
      this.userInfo?.canAccessPlatformAdminMenu()
      || this.canSeeDashboardsAdmin()
      || (this.userInfo?.canAccessOemDealerAdminMenu() ?? false)
    ) ?? false;
  }


  canSeeOemDealersMenu(): boolean {
    return this.canSeeOemDealersAdmin()
      || this.canSeeContextualOemDealerAdmin()
      || (this.userInfo?.canAccessOemDealerAdminMenu() ?? false);
  }

  navigateToVendedoresAdmin(event?: MouseEvent): void {
    if (
      event &&
      (event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey)
    ) {
      return;
    }

    event?.preventDefault();
    event?.stopImmediatePropagation();

    if (this.canSeeOemDealersAdmin()) {
      this.toggleSidebar();
      this.navReload();
      this.router.navigate(['/admin/advertisers/list']);
      return;
    }
    this.navigateToOemDealerAdmin('admin');
  }

  canSeeContextualOemDealerAdmin(): boolean {
    return this.userInfo?.canViewContextualOemDealerAdmin() ?? false;
  }

  canSeeDealersAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasPermission(Permission.VIEW_DEALERS);


    return hasLegacyRole || hasPermission;
  }

  canSeeDealerProspectsAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasAnyPermission(
      Permission.GLOBAL_ACCESS,
      Permission.MANAGE_DEALER_PROSPECTS,
    );

    return !!hasLegacyRole || !!hasPermission;
  }

  canSeeOemDealersAdmin(): boolean {
    return this.canSeeOemDealersAdminFrom(this.userInfo);
  }

  private canSeeOemDealersAdminFrom(user: UserInfo | null | undefined): boolean {
    return user?.canViewOemDealersPlatform() ?? false;
  }

  canSeeBankslipsAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasPermission(Permission.VIEW_BANKSLIPS);

    return hasLegacyRole || hasPermission;
  }

  canSeeFipeAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasPermission(Permission.MANAGE_FIPE);

    return hasLegacyRole || hasPermission;
  }

  canSeeBannersAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasPermission(Permission.MANAGE_BANNERS);

    return hasLegacyRole || hasPermission;
  }

  canSeeHomeAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasPermission(Permission.MANAGE_HOME);

    return hasLegacyRole || hasPermission;
  }

  canSeeSlugAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasPermission(Permission.MANAGE_SLUG);

    return hasLegacyRole || hasPermission;
  }

  canSeeRegionalsAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasPermission(Permission.MANAGE_REGIONALS);

    return hasLegacyRole || hasPermission;
  }

  canSeeCancellationRequests(): boolean {
    const hasLegacyRole = this.userInfo?.hasRoles(...LEGACY_TRANSFER_ROUTE_ROLES);
    const hasPermission = this.userInfo?.hasAnyPermission(
      Permission.GLOBAL_ACCESS,
      Permission.MANAGE_AUCTION_CANCELLATION_REQUESTS,
    );

    return !!hasLegacyRole || !!hasPermission;
  }

  canSeeCancellationRefundsAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);
    const hasPermission = this.userInfo?.hasAnyPermission(
      Permission.GLOBAL_ACCESS,
      Permission.MANAGE_AUCTION_REFUNDS,
    );

    return !!hasLegacyRole || !!hasPermission;
  }

  canSeeDashboardsAdmin(): boolean {
    const hasLegacyRole = this.userInfo?.hasRole(Role.ADMIN);

    return hasLegacyRole || this.userInfo?.hasPermission(Permission.GLOBAL_ACCESS);
  }

  hasPermissionRepasseImportEvaluations() {
    const hasRoles = this.userInfo?.hasRoles(
      Role.ADMIN,
      Role.OEM_DEALER_MANAGER,
      Role.DESK_BUY,
      Role.SELLER_MANAGER,
      Role.SELLER
    )

    const hasPermission = this.userInfo?.hasAnyPermission(Permission.IMPORT_MASS_EVALUATIONS);

    return hasRoles || hasPermission;
  }

  hasPermissionToViewSellReports() {
    return this.userInfo?.hasPermissionOrLegacy(Permission.VIEW_REPORT_SELL);
  }

  goToDashboards(): void {
    let oemDealerId = this.userInfo.oemDealerId;
    let hasPluginRaioX: boolean;
    if (typeof (oemDealerId) === "string") {
      this.oemDealerService.hasPluginRaioX(oemDealerId).subscribe(response => {
        hasPluginRaioX = response;
        if (hasPluginRaioX) {
          this.router.navigateByUrl("captar/dashboards");
        } else {
          this.router.navigateByUrl("dashboard/eval");
        }

        this.toggleSidebar();
      })
    } else {
      this.router.navigateByUrl("dashboard/eval");
      this.toggleSidebar();
    }
  }
}
